import React, { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  PiggyBank,
  Calendar,
  Download,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  LabelList,
  ReferenceLine,
} from "recharts";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const GROUP_COLORS = {
  Income: "#10B981",
  Bills: "#F59E0B",
  Needs: "#3B82F6",
  Wants: "#8B5CF6",
};

const formatCurrency = (value) => {
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(absValue);
  return value < 0 ? `-${formatted}` : formatted;
};

export default function Dashboard() {
  const currentYear = new Date().getFullYear();
  const [summary, setSummary] = useState(null);
  const [budgetGoals, setBudgetGoals] = useState([]);
  const [retirementSummary, setRetirementSummary] = useState(null);
  const [otherSavings, setOtherSavings] = useState(null);
  const [monthlyBreakdown, setMonthlyBreakdown] = useState(null);
  const [detailedBreakdown, setDetailedBreakdown] = useState(null);
  const [yoyData, setYoyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(currentYear);
  const [viewMode, setViewMode] = useState("ytd"); // "ytd" or "fy"
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // YoY comparison state
  const [showLastYear, setShowLastYear] = useState(false);
  const [showNextYear, setShowNextYear] = useState(false);
  const [projectionMethod, setProjectionMethod] = useState("even"); // "even" or "seasonal"
  
  // Drill-down state for monthly breakdown
  const [expandedRows, setExpandedRows] = useState({}); // { income: false, Bills: false, Needs: false, Wants: false, "cat-id": false }
  
  // Budget variance state
  const [varianceData, setVarianceData] = useState(null);
  const [varianceYear, setVarianceYear] = useState(currentYear - 1); // Default to last year (closed)
  const [varianceMode, setVarianceMode] = useState("dollars"); // "dollars" or "percent"
  const [showAllVariance, setShowAllVariance] = useState(false);

  useEffect(() => {
    fetchData();
  }, [year, viewMode]);
  
  // Fetch variance data when variance year changes
  useEffect(() => {
    fetchVarianceData();
  }, [varianceYear]);
  
  // Fetch YoY data when comparison options change
  useEffect(() => {
    if (showLastYear || showNextYear) {
      fetchYoyData();
    } else {
      setYoyData(null);
    }
  }, [year, showLastYear, showNextYear, projectionMethod]);

  const fetchVarianceData = async () => {
    try {
      const res = await api.get(`/budget-variance?year=${varianceYear}`);
      setVarianceData(res.data);
    } catch (error) {
      console.error("Failed to load variance data:", error);
    }
  };

  const fetchYoyData = async () => {
    try {
      const includeYears = [];
      if (showLastYear) includeYears.push(year - 1);
      if (showNextYear) includeYears.push(year + 1);
      
      if (includeYears.length === 0) {
        setYoyData(null);
        return;
      }
      
      const res = await api.get(
        `/monthly-breakdown/yoy?base_year=${year}&include_years=${includeYears.join(",")}&projection_method=${projectionMethod}`
      );
      setYoyData(res.data);
    } catch (error) {
      console.error("Failed to load YoY data:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const currentMonth = new Date().getMonth() + 1;
      const monthParam = viewMode === "fy" ? 12 : currentMonth;
      
      const [summaryRes, goalsRes, retirementRes, otherSavingsRes, monthlyRes, detailedRes] = await Promise.all([
        api.get(`/budget-summary?year=${year}&as_of_month=${monthParam}`),
        api.get(`/budget-goals?year=${year}`),
        api.get(`/retirement-contributions?year=${year}`),
        api.get(`/other-savings?year=${year}`),
        api.get(`/monthly-breakdown?year=${year}`),
        api.get(`/monthly-breakdown/detailed?year=${year}`),
      ]);
      
      setSummary(summaryRes.data);
      setBudgetGoals(goalsRes.data);
      setRetirementSummary(retirementRes.data);
      setOtherSavings(otherSavingsRes.data);
      setMonthlyBreakdown(monthlyRes.data);
      setDetailedBreakdown(detailedRes.data);
    } catch (error) {
      toast.error("Failed to load budget summary");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const currentMonth = new Date().getMonth() + 1;
      const monthParam = viewMode === "fy" ? 12 : currentMonth;
      const res = await api.get(`/report/pdf?year=${year}&as_of_month=${monthParam}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `budget_report_${year}_${viewMode}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report downloaded!");
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const toggleExpandedRow = (rowId) => {
    setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const groupTotals = summary?.group_totals || {};
  const categories = summary?.categories || [];
  const savingsBreakdown = summary?.savings_breakdown || {};

  // Calculate totals - now using savings_breakdown from API for accurate Net Savings
  const incomeActual = groupTotals.Income?.ytd_actual || 0;
  const incomeProrated = groupTotals.Income?.ytd_budget_prorated || 0;
  
  // True expenses exclude Savings group (Wealth-Building goes to investments, not expenses)
  const trueExpenseActual = savingsBreakdown.true_expenses || (
    Math.abs(groupTotals.Bills?.ytd_actual || 0) +
    Math.abs(groupTotals.Needs?.ytd_actual || 0) +
    Math.abs(groupTotals.Wants?.ytd_actual || 0)
  );
  
  // Savings outflows (money going TO investment/savings accounts)
  const savingsOutflow = savingsBreakdown.savings_outflow || 
    Math.abs(groupTotals.Savings?.ytd_actual || 0);
  
  // Legacy expense calculation for display (includes ALL outflows)
  const totalExpenseActual = trueExpenseActual + savingsOutflow;
  const totalExpenseProrated = (groupTotals.Bills?.ytd_budget_prorated || 0) +
    (groupTotals.Needs?.ytd_budget_prorated || 0) +
    (groupTotals.Wants?.ytd_budget_prorated || 0);

  // Calculate savings goal from budget goals (excluding N/A goals)
  const incomeGoalTotal = budgetGoals
    .filter(g => g.period_type !== "na" && categories.find(c => c.category_id === g.category_id)?.group === "Income")
    .reduce((sum, g) => sum + (g.annual_amount || 0), 0);
  const expenseGoalTotal = budgetGoals
    .filter(g => {
      if (g.period_type === "na") return false; // Exclude N/A goals
      const cat = categories.find(c => c.category_id === g.category_id);
      return cat && ["Bills", "Needs", "Wants"].includes(cat.group);
    })
    .reduce((sum, g) => sum + (g.annual_amount || 0), 0);
  const annualSavingsGoal = incomeGoalTotal - expenseGoalTotal;
  
  // Prorated savings goal based on time elapsed
  const proratedSavingsGoal = annualSavingsGoal * (summary?.proration_factor || 1);
  
  // Corrected Net Savings = Income - True Expenses + Savings Outflows
  // This properly accounts for Wealth-Building as savings, not expense
  const actualSavings = savingsBreakdown.net_savings || (incomeActual - trueExpenseActual + savingsOutflow);

  // Percentage calculations
  const incomeVsTarget = incomeProrated > 0 ? ((incomeActual - incomeProrated) / incomeProrated) * 100 : 0;
  const expenseVsTarget = totalExpenseProrated > 0 ? ((totalExpenseActual - totalExpenseProrated) / totalExpenseProrated) * 100 : 0;
  const savingsVsTarget = proratedSavingsGoal > 0 ? ((actualSavings - proratedSavingsGoal) / proratedSavingsGoal) * 100 : 0;

  // Chart data with variance calculations
  const chartData = ["Income", "Bills", "Needs", "Wants"].map((group) => {
    const budget = Math.abs(groupTotals[group]?.ytd_budget_prorated || 0);
    const actual = Math.abs(groupTotals[group]?.ytd_actual || 0);
    const variance = actual - budget;
    const variancePercent = budget > 0 ? (variance / budget) * 100 : 0;
    // For Income: positive variance is good (earned more than expected)
    // For Expenses: negative variance is good (spent less than expected)
    const isGood = group === "Income" ? variance >= 0 : variance <= 0;
    
    return {
      name: group,
      "Prorated Budget": budget,
      "Actual": actual,
      color: GROUP_COLORS[group],
      variance,
      variancePercent,
      isGood,
    };
  });

  // Group categories by type maintaining original order
  const incomeCategories = categories.filter(c => c.group === "Income");
  const billsCategories = categories.filter(c => c.group === "Bills");
  const needsCategories = categories.filter(c => c.group === "Needs");
  const wantsCategories = categories.filter(c => c.group === "Wants");

  const renderCategoryTable = (cats, groupName) => {
    if (cats.length === 0) return null;
    const filteredCats = cats.filter(c => c.annual_budget > 0 || Math.abs(c.ytd_actual) > 0);
    if (filteredCats.length === 0) return null;

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500">Category</th>
              <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500">Annual Budget</th>
              <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500">YTD Budget</th>
              <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500">YTD Actual</th>
              <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500">Variance</th>
            </tr>
          </thead>
          <tbody>
            {filteredCats.map((cat) => {
              const variance = cat.group === "Income" 
                ? cat.ytd_actual - cat.ytd_budget_prorated
                : cat.ytd_budget_prorated - Math.abs(cat.ytd_actual);
              const variancePercent = cat.ytd_budget_prorated !== 0 
                ? (variance / Math.abs(cat.ytd_budget_prorated)) * 100 
                : 0;
              
              return (
                <tr key={cat.category_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2 px-4">
                    <span className="flex items-center gap-2">
                      {cat.emoji && <span className="text-sm">{cat.emoji}</span>}
                      <span className="text-sm text-slate-900">{cat.category_name}</span>
                    </span>
                  </td>
                  <td className="py-2 px-4 text-right font-mono text-sm text-slate-600">
                    {formatCurrency(cat.annual_budget)}
                  </td>
                  <td className="py-2 px-4 text-right font-mono text-sm text-slate-500">
                    {formatCurrency(cat.ytd_budget_prorated)}
                  </td>
                  <td className="py-2 px-4 text-right font-mono text-sm">
                    {formatCurrency(cat.ytd_actual)}
                  </td>
                  <td className={`py-2 px-4 text-right font-mono text-sm ${
                    variance >= 0 ? "text-emerald-600" : "text-rose-600"
                  }`}>
                    {variance >= 0 ? "+" : ""}{formatCurrency(variance)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-slide-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight text-slate-900">
            Budget Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Track your spending against your {year} goals
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={`${year}-${viewMode}`} onValueChange={(v) => {
            const [y, mode] = v.split("-");
            setYear(Number(y));
            setViewMode(mode);
          }}>
            <SelectTrigger className="w-[140px]" data-testid="year-view-select">
              <Calendar className="h-4 w-4 mr-2 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Generate year options dynamically based on current year */}
              {(() => {
                const currentYear = new Date().getFullYear();
                const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
                return years.flatMap(y => {
                  if (y === currentYear) {
                    // Current year: show both YTD and Full Year
                    return [
                      <SelectItem key={`${y}-ytd`} value={`${y}-ytd`}>{y} YTD</SelectItem>,
                      <SelectItem key={`${y}-fy`} value={`${y}-fy`}>{y} Full Year</SelectItem>
                    ];
                  } else {
                    // Past/future years: only Full Year
                    return [
                      <SelectItem key={`${y}-fy`} value={`${y}-fy`}>{y} Full Year</SelectItem>
                    ];
                  }
                });
              })()}
            </SelectContent>
          </Select>

          <Button
            onClick={downloadPdf}
            disabled={downloadingPdf}
            variant="outline"
            className="gap-2"
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4" />
            {downloadingPdf ? "Generating..." : "PDF"}
          </Button>
        </div>
      </div>

      {/* Summary Cards - Main 3 + Retirement tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Income Card */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Income ({viewMode === "fy" ? "Full Year" : "YTD"})</p>
                <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">
                  {formatCurrency(incomeActual)}
                </p>
                <p className={`text-xs mt-1 font-medium ${
                  incomeVsTarget >= 0 ? "text-emerald-600" : "text-rose-600"
                }`}>
                  {incomeVsTarget >= 0 ? "+" : ""}{incomeVsTarget.toFixed(1)}% vs target
                  <span className="text-slate-400 font-normal ml-1">
                    ({formatCurrency(incomeProrated)})
                  </span>
                </p>
              </div>
              <div className={`p-3 rounded-lg ${incomeVsTarget >= 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                <TrendingUp className={`h-6 w-6 ${incomeVsTarget >= 0 ? "text-emerald-600" : "text-rose-600"}`} strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses Card - Shows True Expenses (excludes Savings/Wealth-Building) */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  {savingsOutflow > 0 ? "True Expenses" : "Expenses"} ({viewMode === "fy" ? "Full Year" : "YTD"})
                </p>
                <p className="text-2xl font-bold font-mono text-rose-600 mt-1">
                  {formatCurrency(trueExpenseActual)}
                </p>
                <p className={`text-xs mt-1 font-medium ${
                  expenseVsTarget <= 0 ? "text-emerald-600" : "text-rose-600"
                }`}>
                  {expenseVsTarget >= 0 ? "+" : ""}{expenseVsTarget.toFixed(1)}% vs target
                  <span className="text-slate-400 font-normal ml-1">
                    ({formatCurrency(totalExpenseProrated)})
                  </span>
                </p>
              </div>
              <div className={`p-3 rounded-lg ${expenseVsTarget <= 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                <TrendingDown className={`h-6 w-6 ${expenseVsTarget <= 0 ? "text-emerald-600" : "text-rose-600"}`} strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Savings Card - Shows formula breakdown */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-500">Net Savings ({viewMode === "fy" ? "Full Year" : "YTD"})</p>
                <p className={`text-2xl font-bold font-mono mt-1 ${
                  actualSavings >= 0 ? "text-emerald-600" : "text-rose-600"
                }`}>
                  {formatCurrency(actualSavings)}
                </p>
                {/* Formula breakdown when savings outflow exists */}
                {savingsOutflow > 0 ? (
                  <div className="text-xs mt-2 space-y-0.5 font-mono text-slate-500">
                    <div className="flex justify-between">
                      <span>Income:</span>
                      <span className="text-emerald-600">{formatCurrency(incomeActual)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>− Expenses:</span>
                      <span className="text-rose-600">{formatCurrency(trueExpenseActual)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>+ Savings:</span>
                      <span className="text-sky-600">{formatCurrency(savingsOutflow)}</span>
                    </div>
                  </div>
                ) : (
                  <p className={`text-xs mt-1 font-medium ${
                    savingsVsTarget >= 0 ? "text-emerald-600" : "text-rose-600"
                  }`}>
                    {savingsVsTarget >= 0 ? "+" : ""}{savingsVsTarget.toFixed(1)}% vs goal
                    <span className="text-slate-400 font-normal ml-1">
                      ({formatCurrency(proratedSavingsGoal)})
                    </span>
                  </p>
                )}
              </div>
              <div className={`p-3 rounded-lg ${actualSavings >= 0 ? "bg-emerald-50" : "bg-rose-50"}`}>
                <PiggyBank className={`h-6 w-6 ${actualSavings >= 0 ? "text-emerald-600" : "text-rose-600"}`} strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Retirement + Wealth-Building + Other Savings + Total Net Savings Row */}
      {(retirementSummary?.contributions?.length > 0 || retirementSummary?.total_annual_goal > 0 || otherSavings?.total > 0 || savingsOutflow > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 401k Contributions */}
          <Card className="border-sky-200 bg-sky-50/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-sky-600">401k ({viewMode === "fy" ? "Goal" : "YTD"})</p>
                  <p className="text-xl font-bold font-mono text-sky-700 mt-0.5">
                    {formatCurrency(viewMode === "fy" ? retirementSummary?.total_annual_goal || 0 : retirementSummary?.total_ytd || 0)}
                  </p>
                  {retirementSummary?.contributions?.length > 0 && (
                    <p className="text-xs text-sky-600 mt-0.5">
                      {retirementSummary.contributions.map(c => c.person).join(" + ")}
                    </p>
                  )}
                </div>
                <div className="p-2 rounded-lg bg-sky-100">
                  <PiggyBank className="h-5 w-5 text-sky-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Wealth-Building / Savings Outflows - money going TO investment accounts from YNAB */}
          {savingsOutflow > 0 && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-amber-600">Wealth-Building ({viewMode === "fy" ? "Full Year" : "YTD"})</p>
                    <p className="text-xl font-bold font-mono text-amber-700 mt-0.5">
                      {formatCurrency(savingsOutflow)}
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      Invested via YNAB
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-amber-100">
                    <TrendingUp className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Other Savings (HSA, etc.) */}
          <Card className="border-violet-200 bg-violet-50/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-violet-600">Other Savings (HSA, etc.)</p>
                  <p className="text-xl font-bold font-mono text-violet-700 mt-0.5">
                    {formatCurrency(otherSavings?.total || 0)}
                  </p>
                  {otherSavings?.items?.length > 0 && (
                    <p className="text-xs text-violet-600 mt-0.5">
                      {otherSavings.items.map(i => i.name.split(' - ')[0]).filter((v, i, a) => a.indexOf(v) === i).join(", ")}
                    </p>
                  )}
                </div>
                <div className="p-2 rounded-lg bg-violet-100">
                  <TrendingUp className="h-5 w-5 text-violet-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Net Savings (including all savings) */}
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-emerald-600">Total Net Wealth Built ({viewMode === "fy" ? "Goal" : "YTD"})</p>
                  <p className="text-xl font-bold font-mono text-emerald-700 mt-0.5">
                    {formatCurrency(
                      actualSavings + 
                      (viewMode === "fy" ? retirementSummary?.total_annual_goal || 0 : retirementSummary?.total_ytd || 0) +
                      (otherSavings?.total || 0)
                    )}
                  </p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Cash: {formatCurrency(actualSavings)} + Retirement: {formatCurrency((retirementSummary?.total_ytd || 0) + (otherSavings?.total || 0))}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-emerald-100">
                  <TrendingUp className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monthly Income vs Expenses Breakdown - for YNAB comparison */}
      <Collapsible open={!collapsedSections.monthlyBreakdown} onOpenChange={() => toggleSection('monthlyBreakdown')}>
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {collapsedSections.monthlyBreakdown ? (
                    <ChevronRight className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  )}
                  <CardTitle className="font-['Outfit'] text-lg">Monthly Income vs Expenses</CardTitle>
                </div>
                <Badge variant="secondary" className="bg-slate-100">
                  Compare with YNAB
                </Badge>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              {/* YoY Comparison Controls */}
              <div className="flex flex-wrap items-center gap-4 mb-4 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="show-last-year" 
                    checked={showLastYear}
                    onCheckedChange={setShowLastYear}
                    data-testid="show-last-year-checkbox"
                  />
                  <Label htmlFor="show-last-year" className="text-sm text-slate-600 cursor-pointer">
                    Show {year - 1}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="show-next-year" 
                    checked={showNextYear}
                    onCheckedChange={setShowNextYear}
                    data-testid="show-next-year-checkbox"
                  />
                  <Label htmlFor="show-next-year" className="text-sm text-slate-600 cursor-pointer">
                    Show {year + 1}
                  </Label>
                </div>
                {showNextYear && (
                  <div className="flex items-center gap-2 ml-4 pl-4 border-l border-slate-200">
                    <span className="text-xs text-slate-500">Projection:</span>
                    <Select value={projectionMethod} onValueChange={setProjectionMethod}>
                      <SelectTrigger className="h-7 w-[110px] text-xs" data-testid="projection-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="even">Even (÷12)</SelectItem>
                        <SelectItem value="seasonal">Seasonal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              {monthlyBreakdown && detailedBreakdown && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500 sticky left-0 bg-white min-w-[140px]"></th>
                        {monthlyBreakdown.months.map((m) => (
                          <th key={m.month} className="text-center py-2 px-2 text-xs font-semibold text-slate-500 min-w-[70px]">
                            {m.month_name.substring(0, 3)}
                            {yoyData && m.month === yoyData.current_month && (
                              <span className="block text-[10px] text-amber-600 font-normal">MTD</span>
                            )}
                          </th>
                        ))}
                        <th className="text-center py-2 px-2 text-xs font-semibold text-slate-700 min-w-[80px] bg-slate-50">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Income Section - Expandable */}
                      <tr 
                        className="border-b border-slate-100 cursor-pointer hover:bg-emerald-50/50"
                        onClick={() => toggleExpandedRow('income')}
                      >
                        <td className="py-2 px-2 font-medium text-emerald-600 sticky left-0 bg-white">
                          <span className="flex items-center gap-1">
                            {expandedRows.income ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                            Income
                          </span>
                        </td>
                        {detailedBreakdown.income.monthly_values.map((val, idx) => (
                          <td key={idx} className="py-2 px-2 text-center font-mono text-emerald-600">
                            {val > 0 ? formatCurrency(val) : '-'}
                          </td>
                        ))}
                        <td className="py-2 px-2 text-center font-mono font-semibold text-emerald-700 bg-slate-50">
                          {formatCurrency(detailedBreakdown.income.total)}
                        </td>
                      </tr>
                      {/* Income categories (expanded) */}
                      {expandedRows.income && detailedBreakdown.income.categories.map((cat) => (
                        <tr key={cat.category_id} className="border-b border-slate-50 bg-emerald-50/20">
                          <td className="py-1.5 px-2 pl-6 text-xs text-slate-600 sticky left-0 bg-emerald-50/20">
                            {cat.emoji && <span className="mr-1">{cat.emoji}</span>}
                            {cat.category_name}
                          </td>
                          {cat.monthly_values.map((val, idx) => (
                            <td key={idx} className="py-1.5 px-2 text-center font-mono text-xs text-slate-600">
                              {val > 0 ? formatCurrency(val) : '-'}
                            </td>
                          ))}
                          <td className="py-1.5 px-2 text-center font-mono text-xs font-medium text-slate-600 bg-slate-50/50">
                            {formatCurrency(cat.total)}
                          </td>
                        </tr>
                      ))}
                      {/* Income comparison rows */}
                      {yoyData && yoyData.years.filter(y => y.year !== year).map((yearData) => (
                        <tr key={`income-${yearData.year}`} className="border-b border-slate-50 bg-slate-50/30">
                          <td className="py-1.5 px-2 text-xs text-slate-500 sticky left-0 bg-slate-50/30">
                            ({yearData.year})
                          </td>
                          {yearData.months.map((m) => {
                            const hasData = m.income > 0 || m.expenses > 0;
                            return (
                              <td 
                                key={m.month} 
                                className={`py-1.5 px-2 text-center font-mono text-xs ${
                                  m.is_projection ? 'italic text-slate-400' : 'text-slate-500'
                                }`}
                              >
                                {hasData || m.is_projection ? formatCurrency(m.income) : '-'}
                              </td>
                            );
                          })}
                          <td className={`py-1.5 px-2 text-center font-mono text-xs font-medium bg-slate-100/50 ${
                            yearData.projection_method ? 'italic text-slate-400' : 'text-slate-500'
                          }`}>
                            {formatCurrency(yearData.totals.income)}
                          </td>
                        </tr>
                      ))}
                      
                      {/* Expenses Section - Expandable by Group */}
                      <tr 
                        className="border-b border-slate-100 cursor-pointer hover:bg-rose-50/50"
                        onClick={() => toggleExpandedRow('expenses')}
                      >
                        <td className="py-2 px-2 font-medium text-rose-600 sticky left-0 bg-white">
                          <span className="flex items-center gap-1">
                            {expandedRows.expenses ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                            Expenses
                          </span>
                        </td>
                        {monthlyBreakdown.months.map((m) => {
                          const hasData = m.income > 0 || m.expenses > 0;
                          return (
                            <td key={m.month} className="py-2 px-2 text-center font-mono text-rose-600">
                              {hasData ? formatCurrency(m.expenses) : '-'}
                            </td>
                          );
                        })}
                        <td className="py-2 px-2 text-center font-mono font-semibold text-rose-700 bg-slate-50">
                          {formatCurrency(monthlyBreakdown.totals.expenses)}
                        </td>
                      </tr>
                      
                      {/* Expense Groups (Bills, Needs, Wants) - expanded */}
                      {expandedRows.expenses && detailedBreakdown.expenses_by_group.map((group) => (
                        <React.Fragment key={group.group}>
                          {/* Group header row */}
                          <tr 
                            className={`border-b border-slate-100 cursor-pointer hover:bg-slate-100/50 ${
                              group.group === 'Bills' ? 'bg-amber-50/30' : 
                              group.group === 'Needs' ? 'bg-blue-50/30' : 'bg-purple-50/30'
                            }`}
                            onClick={(e) => { e.stopPropagation(); toggleExpandedRow(group.group); }}
                          >
                            <td className={`py-1.5 px-2 pl-4 font-medium text-xs sticky left-0 ${
                              group.group === 'Bills' ? 'text-amber-700 bg-amber-50/30' : 
                              group.group === 'Needs' ? 'text-blue-700 bg-blue-50/30' : 'text-purple-700 bg-purple-50/30'
                            }`}>
                              <span className="flex items-center gap-1">
                                {expandedRows[group.group] ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <ChevronRight className="h-3 w-3" />
                                )}
                                {group.group}
                              </span>
                            </td>
                            {group.monthly_values.map((val, idx) => (
                              <td key={idx} className={`py-1.5 px-2 text-center font-mono text-xs ${
                                group.group === 'Bills' ? 'text-amber-700' : 
                                group.group === 'Needs' ? 'text-blue-700' : 'text-purple-700'
                              }`}>
                                {val > 0 ? formatCurrency(val) : '-'}
                              </td>
                            ))}
                            <td className={`py-1.5 px-2 text-center font-mono text-xs font-semibold bg-slate-50/50 ${
                              group.group === 'Bills' ? 'text-amber-700' : 
                              group.group === 'Needs' ? 'text-blue-700' : 'text-purple-700'
                            }`}>
                              {formatCurrency(group.total)}
                            </td>
                          </tr>
                          
                          {/* Category rows within group */}
                          {expandedRows[group.group] && group.categories.map((cat) => (
                            <tr key={cat.category_id} className="border-b border-slate-50 bg-slate-50/20">
                              <td className="py-1 px-2 pl-8 text-xs text-slate-500 sticky left-0 bg-slate-50/20">
                                {cat.emoji && <span className="mr-1">{cat.emoji}</span>}
                                {cat.category_name}
                              </td>
                              {cat.monthly_values.map((val, idx) => (
                                <td key={idx} className="py-1 px-2 text-center font-mono text-xs text-slate-500">
                                  {val > 0 ? formatCurrency(val) : '-'}
                                </td>
                              ))}
                              <td className="py-1 px-2 text-center font-mono text-xs text-slate-500 bg-slate-50/30">
                                {formatCurrency(cat.total)}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                      
                      {/* Expenses comparison rows */}
                      {yoyData && yoyData.years.filter(y => y.year !== year).map((yearData) => (
                        <tr key={`expenses-${yearData.year}`} className="border-b border-slate-50 bg-slate-50/30">
                          <td className="py-1.5 px-2 text-xs text-slate-500 sticky left-0 bg-slate-50/30">
                            ({yearData.year})
                          </td>
                          {yearData.months.map((m) => {
                            const hasData = m.income > 0 || m.expenses > 0;
                            return (
                              <td 
                                key={m.month} 
                                className={`py-1.5 px-2 text-center font-mono text-xs ${
                                  m.is_projection ? 'italic text-slate-400' : 'text-slate-500'
                                }`}
                              >
                                {hasData || m.is_projection ? formatCurrency(m.expenses) : '-'}
                              </td>
                            );
                          })}
                          <td className={`py-1.5 px-2 text-center font-mono text-xs font-medium bg-slate-100/50 ${
                            yearData.projection_method ? 'italic text-slate-400' : 'text-slate-500'
                          }`}>
                            {formatCurrency(yearData.totals.expenses)}
                          </td>
                        </tr>
                      ))}
                      
                      {/* Savings Section - only show if there's savings data */}
                      {detailedBreakdown?.savings_group && detailedBreakdown.savings_group.total > 0 && (
                        <>
                          <tr 
                            className="border-b border-slate-100 cursor-pointer hover:bg-amber-50/50"
                            onClick={() => toggleExpandedRow('savings')}
                          >
                            <td className="py-2 px-2 font-medium text-amber-600 sticky left-0 bg-white">
                              <span className="flex items-center gap-1">
                                {expandedRows.savings ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <ChevronRight className="h-3 w-3" />
                                )}
                                + Savings
                              </span>
                            </td>
                            {detailedBreakdown.savings_group.monthly_values.map((val, idx) => (
                              <td key={idx} className="py-2 px-2 text-center font-mono text-amber-600">
                                {val > 0 ? formatCurrency(val) : '-'}
                              </td>
                            ))}
                            <td className="py-2 px-2 text-center font-mono font-semibold text-amber-700 bg-slate-50">
                              {formatCurrency(detailedBreakdown.savings_group.total)}
                            </td>
                          </tr>
                          
                          {/* Savings categories - expanded */}
                          {expandedRows.savings && detailedBreakdown.savings_group.categories.map((cat) => (
                            <tr key={cat.category_id} className="border-b border-slate-50 bg-amber-50/20">
                              <td className="py-1 px-2 pl-8 text-xs text-amber-600 sticky left-0 bg-amber-50/20">
                                {cat.emoji && <span className="mr-1">{cat.emoji}</span>}
                                {cat.category_name}
                              </td>
                              {cat.monthly_values.map((val, idx) => (
                                <td key={idx} className="py-1 px-2 text-center font-mono text-xs text-amber-600">
                                  {val > 0 ? formatCurrency(val) : '-'}
                                </td>
                              ))}
                              <td className="py-1 px-2 text-center font-mono text-xs text-amber-700 bg-amber-50/30">
                                {formatCurrency(cat.total)}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                      
                      {/* Net Section */}
                      <tr className="bg-slate-50 font-semibold">
                        <td className="py-2 px-2 font-medium text-slate-700 sticky left-0 bg-slate-50">Net</td>
                        {monthlyBreakdown.months.map((m) => {
                          const hasData = m.income > 0 || m.expenses > 0;
                          const currMonth = new Date().getMonth() + 1;
                          const isFuture = m.month > currMonth && year >= new Date().getFullYear();
                          return (
                            <td 
                              key={m.month} 
                              className={`py-2 px-2 text-center font-mono ${m.net >= 0 ? 'text-emerald-700' : 'text-rose-700'} ${!hasData && isFuture ? 'opacity-40' : ''}`}
                            >
                              {hasData ? formatCurrency(m.net) : '-'}
                            </td>
                          );
                        })}
                        <td className={`py-2 px-2 text-center font-mono font-bold bg-slate-100 ${monthlyBreakdown.totals.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {formatCurrency(monthlyBreakdown.totals.net)}
                        </td>
                      </tr>
                      {/* Net comparison rows */}
                      {yoyData && yoyData.years.filter(y => y.year !== year).map((yearData) => (
                        <tr key={`net-${yearData.year}`} className="bg-slate-100/50">
                          <td className="py-1.5 px-2 text-xs text-slate-500 sticky left-0 bg-slate-100/50">
                            ({yearData.year})
                          </td>
                          {yearData.months.map((m) => {
                            const hasData = m.income > 0 || m.expenses > 0;
                            return (
                              <td 
                                key={m.month} 
                                className={`py-1.5 px-2 text-center font-mono text-xs ${
                                  m.is_projection ? 'italic' : ''
                                } ${m.net >= 0 ? 'text-emerald-600' : 'text-rose-600'} ${
                                  m.is_projection ? 'opacity-60' : ''
                                }`}
                              >
                                {hasData || m.is_projection ? formatCurrency(m.net) : '-'}
                              </td>
                            );
                          })}
                          <td className={`py-1.5 px-2 text-center font-mono text-xs font-medium bg-slate-100 ${
                            yearData.projection_method ? 'italic opacity-60' : ''
                          } ${yearData.totals.net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(yearData.totals.net)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Legend for projections */}
              {yoyData && yoyData.years.some(y => y.projection_method) && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                  <span className="italic text-slate-400">Italic values</span>
                  <span>= projections based on {projectionMethod === "seasonal" ? "historical seasonality" : "even distribution"}</span>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Budget vs Actual Chart - Full Width */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-['Outfit'] text-lg">Budget vs Actual by Group</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={60} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                  }}
                />
                <Legend 
                  verticalAlign="top" 
                  height={36}
                  formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                />
                <Bar dataKey="Prorated Budget" fill="#CBD5E1" radius={[0, 4, 4, 0]} />
                <Bar dataKey="Actual" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                  <LabelList 
                    dataKey="Actual"
                    position="right"
                    formatter={(value, name, props) => {
                      const entry = chartData[props.index];
                      if (!entry) return '';
                      const sign = entry.variance >= 0 ? '+' : '';
                      const percentSign = entry.variancePercent >= 0 ? '+' : '';
                      return `${sign}$${Math.abs(entry.variance / 1000).toFixed(0)}k (${percentSign}${entry.variancePercent.toFixed(0)}%)`;
                    }}
                    style={{ 
                      fontSize: '11px', 
                      fontFamily: 'ui-monospace, monospace',
                    }}
                    fill={(entry) => entry.isGood ? '#059669' : '#DC2626'}
                    content={(props) => {
                      const { x, y, width, height, value, index } = props;
                      const entry = chartData[index];
                      if (!entry) return null;
                      const sign = entry.variance >= 0 ? '+' : '';
                      const percentSign = entry.variancePercent >= 0 ? '+' : '';
                      const label = `${sign}$${Math.abs(entry.variance / 1000).toFixed(0)}k (${percentSign}${entry.variancePercent.toFixed(0)}%)`;
                      return (
                        <text 
                          x={x + width + 8} 
                          y={y + height / 2 + 4} 
                          fill={entry.isGood ? '#059669' : '#DC2626'}
                          fontSize={11}
                          fontFamily="ui-monospace, monospace"
                        >
                          {label}
                        </text>
                      );
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Category Details - Collapsible Sections */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-['Outfit'] text-lg">Category Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Income Section */}
          <Collapsible open={!collapsedSections.income} onOpenChange={() => toggleSection('income')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                <div className="flex items-center gap-2">
                  {collapsedSections.income ? (
                    <ChevronRight className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-emerald-600" />
                  )}
                  <span className="font-semibold text-emerald-800">Income</span>
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                    {incomeCategories.filter(c => c.annual_budget > 0 || Math.abs(c.ytd_actual) > 0).length} categories
                  </Badge>
                </div>
                <div className="text-right">
                  <span className="font-mono font-semibold text-emerald-700">
                    {formatCurrency(incomeActual)}
                  </span>
                  <span className="text-xs text-slate-400 font-normal ml-2">
                    / {formatCurrency(incomeProrated)} plan
                  </span>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              {renderCategoryTable(incomeCategories, "Income")}
            </CollapsibleContent>
          </Collapsible>

          {/* Expenses Header */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-sm font-semibold text-slate-500 mb-3">EXPENSES</p>
          </div>

          {/* Bills Section */}
          <Collapsible open={!collapsedSections.bills} onOpenChange={() => toggleSection('bills')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                <div className="flex items-center gap-2">
                  {collapsedSections.bills ? (
                    <ChevronRight className="h-4 w-4 text-amber-600" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-amber-600" />
                  )}
                  <span className="font-semibold text-amber-800">Bills</span>
                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                    {billsCategories.filter(c => c.annual_budget > 0 || Math.abs(c.ytd_actual) > 0).length} categories
                  </Badge>
                </div>
                <div className="text-right">
                  <span className="font-mono font-semibold text-amber-700">
                    {formatCurrency(Math.abs(groupTotals.Bills?.ytd_actual || 0))}
                  </span>
                  <span className="text-xs text-slate-400 font-normal ml-2">
                    / {formatCurrency(groupTotals.Bills?.ytd_budget_prorated || 0)} plan
                  </span>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              {renderCategoryTable(billsCategories, "Bills")}
            </CollapsibleContent>
          </Collapsible>

          {/* Needs Section */}
          <Collapsible open={!collapsedSections.needs} onOpenChange={() => toggleSection('needs')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex items-center gap-2">
                  {collapsedSections.needs ? (
                    <ChevronRight className="h-4 w-4 text-blue-600" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-blue-600" />
                  )}
                  <span className="font-semibold text-blue-800">Needs</span>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {needsCategories.filter(c => c.annual_budget > 0 || Math.abs(c.ytd_actual) > 0).length} categories
                  </Badge>
                </div>
                <div className="text-right">
                  <span className="font-mono font-semibold text-blue-700">
                    {formatCurrency(Math.abs(groupTotals.Needs?.ytd_actual || 0))}
                  </span>
                  <span className="text-xs text-slate-400 font-normal ml-2">
                    / {formatCurrency(groupTotals.Needs?.ytd_budget_prorated || 0)} plan
                  </span>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              {renderCategoryTable(needsCategories, "Needs")}
            </CollapsibleContent>
          </Collapsible>

          {/* Wants Section */}
          <Collapsible open={!collapsedSections.wants} onOpenChange={() => toggleSection('wants')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <div className="flex items-center gap-2">
                  {collapsedSections.wants ? (
                    <ChevronRight className="h-4 w-4 text-purple-600" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-purple-600" />
                  )}
                  <span className="font-semibold text-purple-800">Wants</span>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                    {wantsCategories.filter(c => c.annual_budget > 0 || Math.abs(c.ytd_actual) > 0).length} categories
                  </Badge>
                </div>
                <div className="text-right">
                  <span className="font-mono font-semibold text-purple-700">
                    {formatCurrency(Math.abs(groupTotals.Wants?.ytd_actual || 0))}
                  </span>
                  <span className="text-xs text-slate-400 font-normal ml-2">
                    / {formatCurrency(groupTotals.Wants?.ytd_budget_prorated || 0)} plan
                  </span>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              {renderCategoryTable(wantsCategories, "Wants")}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Budget Variance Analysis */}
      <Collapsible open={!collapsedSections.variance} onOpenChange={() => toggleSection('variance')}>
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {collapsedSections.variance ? (
                    <ChevronRight className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  )}
                  <CardTitle className="font-['Outfit'] text-lg">Budget Variance Analysis</CardTitle>
                </div>
                <Badge variant="secondary" className="bg-slate-100">
                  {varianceYear < currentYear ? "Closed Year Review" : "Current Year Review"}
                </Badge>
              </div>
            </CollapsibleTrigger>
            <p className="text-sm text-slate-500 mt-1 text-left">Where did we overspend or underspend?</p>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              {/* Controls */}
              <div className="flex flex-wrap items-center gap-4 mb-4 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">Year:</span>
                  <Select value={String(varianceYear)} onValueChange={(v) => setVarianceYear(Number(v))}>
                    <SelectTrigger className="w-[100px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[currentYear - 2, currentYear - 1, currentYear].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  <Button 
                    variant={varianceMode === "dollars" ? "secondary" : "ghost"} 
                    size="sm"
                    className="h-7 px-3 text-xs"
                    onClick={() => setVarianceMode("dollars")}
                  >
                    $
                  </Button>
                  <Button 
                    variant={varianceMode === "percent" ? "secondary" : "ghost"} 
                    size="sm"
                    className="h-7 px-3 text-xs"
                    onClick={() => setVarianceMode("percent")}
                  >
                    %
                  </Button>
                </div>
                {varianceData && varianceData.with_budget.length > 10 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs"
                    onClick={() => setShowAllVariance(!showAllVariance)}
                  >
                    {showAllVariance ? "Show Top 10" : `Show All (${varianceData.with_budget.length})`}
                  </Button>
                )}
              </div>

              {varianceData && (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-6 text-center">
                    <div className="p-3 bg-rose-50 rounded-lg">
                      <div className="text-2xl font-bold text-rose-600">{varianceData.summary.categories_overspent}</div>
                      <div className="text-xs text-rose-600">Overspent</div>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">{varianceData.summary.categories_underspent}</div>
                      <div className="text-xs text-emerald-600">Underspent</div>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <div className="text-2xl font-bold text-slate-600">{varianceData.summary.categories_without_budget}</div>
                      <div className="text-xs text-slate-500">No Budget</div>
                    </div>
                  </div>

                  {/* Bar Chart - Categories with Budget */}
                  {varianceData.with_budget.length > 0 && (
                    <div className="h-[350px] mb-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={(showAllVariance ? varianceData.with_budget : varianceData.with_budget.slice(0, 10)).map(item => ({
                            name: item.category_name.length > 15 
                              ? item.category_name.substring(0, 15) + "..." 
                              : item.category_name,
                            fullName: item.category_name,
                            value: varianceMode === "dollars" ? item.variance_dollars : item.variance_percent,
                            budget: item.budget,
                            actual: item.actual,
                            variance_dollars: item.variance_dollars,
                            variance_percent: item.variance_percent,
                            isOverspent: item.is_overspent,
                            group: item.category_group,
                          }))}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                          <XAxis 
                            type="number" 
                            tickFormatter={(val) => varianceMode === "dollars" 
                              ? `${val >= 0 ? '' : '-'}$${Math.abs(val / 1000).toFixed(1)}k`
                              : `${val >= 0 ? '+' : ''}${val.toFixed(0)}%`
                            }
                          />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            tick={{ fontSize: 11 }}
                            width={95}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 shadow-lg rounded-lg border text-sm">
                                    <div className="font-medium mb-1">{data.fullName}</div>
                                    <div className="text-slate-500 text-xs mb-2">{data.group}</div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                      <span className="text-slate-500">Budget:</span>
                                      <span className="text-right font-mono">{formatCurrency(data.budget)}</span>
                                      <span className="text-slate-500">Actual:</span>
                                      <span className="text-right font-mono">{formatCurrency(data.actual)}</span>
                                      <span className="text-slate-500">Variance:</span>
                                      <span className={`text-right font-mono font-medium ${data.isOverspent ? 'text-rose-600' : 'text-emerald-600'}`}>
                                        {formatCurrency(data.variance_dollars)} ({data.variance_percent > 0 ? '+' : ''}{data.variance_percent.toFixed(1)}%)
                                      </span>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={2} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {(showAllVariance ? varianceData.with_budget : varianceData.with_budget.slice(0, 10)).map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.is_overspent ? "#f43f5e" : "#10b981"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Categories without budget */}
                  {varianceData.without_budget.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <p className="text-sm font-medium text-slate-600 mb-3">
                        Categories Without Budget ({varianceData.without_budget.length})
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {varianceData.without_budget.slice(0, showAllVariance ? 100 : 6).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <span className="truncate flex-1 mr-2">
                              {item.emoji && <span className="mr-1">{item.emoji}</span>}
                              {item.category_name}
                            </span>
                            <span className="font-mono text-slate-600">{formatCurrency(item.actual)}</span>
                          </div>
                        ))}
                      </div>
                      {varianceData.without_budget.length > 6 && !showAllVariance && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="mt-2 text-xs w-full"
                          onClick={() => setShowAllVariance(true)}
                        >
                          Show {varianceData.without_budget.length - 6} more...
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Legend */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-6 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-rose-500"></div>
                      <span>Overspent</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-emerald-500"></div>
                      <span>Underspent</span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}
